import { useState } from 'react';
import { isCoursesSite } from '../platform/detect';

/**
 * Форма «Поддержка» / «Задать вопрос».
 * Из Telegram/VK-мини-приложения ответ приходит в бот (контакт не спрашиваем).
 * Из обычного браузера — как на лыжных сайтах (решение владельца 08.09.2026): имя, почта,
 * телефон (необязательно), сообщение. Обращение ложится в «Обращения» дашборда проектом
 * витрины (kurs/beshek), отвечаем письмом оттуда. Telegram и MAX как «куда ответить» убраны.
 * Страница, с которой пришли (?from=… или referrer с нашей витрины), уходит в обращение —
 * владелец видит, о каком уроке вопрос.
 */

function fromPage(): string {
  try {
    const p = new URLSearchParams(window.location.search).get('from') || document.referrer || '';
    const u = new URL(p, window.location.origin);
    return u.origin === window.location.origin && u.pathname !== '/support' ? u.toString() : '';
  } catch { return ''; }
}

export function SupportPage() {
  const [message, setMessage] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorText, setErrorText] = useState('Ошибка отправки. Попробуйте позже.');
  const [from] = useState(fromPage);

  const tg = (window as any).Telegram?.WebApp;
  const params = new URLSearchParams(window.location.search);
  const vkUserId = params.get('vk_user_id') || sessionStorage.getItem('vk_user_id') || '';
  if (params.get('vk_user_id')) sessionStorage.setItem('vk_user_id', params.get('vk_user_id')!);
  const tgUserId = tg?.initDataUnsafe?.user?.id?.toString() || '';
  const inBrowser = !tgUserId && !vkUserId;
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canSend = !!message.trim() && (!inBrowser || emailOk);

  const handleSubmit = async () => {
    if (!canSend) return;
    setStatus('sending');
    const userId = tgUserId || vkUserId || 'unknown';
    const username = tg?.initDataUnsafe?.user?.username || '';
    const platform = vkUserId ? 'vk' : 'telegram';
    const site = isCoursesSite() ? 'kurs' : 'beshek';
    let webUserId = '';
    try { webUserId = localStorage.getItem('beshek_uid') || ''; } catch { /* нет localStorage */ }
    try {
      const ctl = new AbortController();
      const timer = setTimeout(() => ctl.abort(), 15000);
      const res = await fetch('/api/support/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId, username, platform, site, webUserId, message: message.trim(),
          name: name.trim(), contact: email.trim(), contactType: 'email', phone: phone.trim(), page: from,
        }),
        signal: ctl.signal,
      });
      clearTimeout(timer);
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setStatus('sent');
        setMessage('');
      } else {
        setErrorText(d.error ? `Не отправилось: ${d.error}` : 'Ошибка отправки. Попробуйте позже.');
        setStatus('error');
      }
    } catch {
      setErrorText('Сервер не ответил. Попробуйте ещё раз через минуту.');
      setStatus('error');
    }
  };

  const back = () => { window.location.href = from || '/'; };
  const backLabel = from ? '← Вернуться' : '← Меню';
  const field = 'w-full p-3 bg-white border border-[#E1E5E3] text-[#252A2D] rounded-xl placeholder:text-[#252A2D]/40 focus:outline-none focus:border-[#0F766E]/50';

  if (status === 'sent') {
    return (
      <div className="min-h-screen bg-[#F7F7F4] flex flex-col items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="text-7xl mb-4">✅</div>
          <h1 className="text-2xl font-bold text-[#252A2D] mb-2">Отправлено!</h1>
          <p className="text-[#626B73] mb-6">{inBrowser ? 'Ответим письмом на указанную почту' : 'Ответ придёт в этот бот'}</p>
          <div className="space-y-3">
            <button onClick={() => setStatus('idle')} className="w-full py-3 bg-white border border-[#E1E5E3] text-[#252A2D] font-bold rounded-xl hover:bg-white/30 transition">
              Написать ещё
            </button>
            <button onClick={back} className="w-full py-3 bg-white border border-[#E1E5E3] text-[#252A2D] rounded-xl transition">
              {backLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F7F4] flex flex-col p-4">
      <button onClick={back} className="px-3 py-1.5 bg-white border border-[#E1E5E3] rounded-lg text-sm text-[#252A2D] transition-all mb-4 self-start">
        {backLabel}
      </button>

      <div className="flex-1 flex flex-col items-center justify-center max-w-md mx-auto w-full">
        <div className="text-5xl mb-4">📩</div>
        <h1 className="text-2xl font-bold text-[#252A2D] mb-2 text-center">Задать вопрос</h1>
        <p className="text-[#626B73] mb-6 text-center">
          {inBrowser ? 'Ответим письмом, обычно в течение дня' : 'Опишите проблему или задайте вопрос'}
        </p>

        {inBrowser && (
          <div className="w-full space-y-2 mb-2">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ваше имя" maxLength={100} className={field} />
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Почта для ответа" maxLength={200} inputMode="email" autoComplete="email" className={field} />
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Телефон (необязательно)" maxLength={30} inputMode="tel" autoComplete="tel" className={field} />
          </div>
        )}

        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Ваш вопрос..."
          rows={5}
          maxLength={2000}
          className={`${field} resize-none mb-1`}
        />
        <div className="text-right text-[#0F766E] text-xs mb-4 w-full">{message.length}/2000</div>

        {status === 'error' && (
          <div className="bg-red-50 border border-red-300 rounded-xl p-3 mb-4 text-red-700 text-center w-full">{errorText}</div>
        )}

        <button
          onClick={handleSubmit}
          disabled={!canSend || status === 'sending'}
          className="w-full py-4 bg-[#0F766E] hover:bg-[#0B5F59] text-white font-bold text-lg rounded-xl shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {status === 'sending' ? 'Отправка...' : 'Отправить'}
        </button>
        {inBrowser && !emailOk && email.trim() !== '' && (
          <p className="text-red-600 text-xs mt-2">Проверьте почту: нужна вида name@site.ru</p>
        )}
      </div>
    </div>
  );
}
